import React from 'react';
import { Coffee, Palette, Camera, Book, Leaf, Scissors, Utensils, Heart, Database, Zap, Star, ShoppingBag, Store, ChevronDown } from 'lucide-react';

const BusinessIcon: React.FC<{ color: string; icon: React.ElementType; label?: string }> = ({ color, icon: Icon, label }) => (
  <div className="flex flex-col items-center gap-2 sm:gap-3 shrink-0 w-16 sm:w-24">
    <div 
      className={`w-14 h-14 sm:w-20 sm:h-20 rounded-[1rem] sm:rounded-[1.2rem] flex items-center justify-center shadow-lg transform transition-all hover:scale-110 active:animate-jump cursor-pointer active:brightness-110`} 
      style={{ backgroundColor: color }}
    >
      <Icon className="text-white w-7 h-7 sm:w-10 sm:h-10" strokeWidth={1.5} />
    </div>
    {label && (
      <span className="block text-[7px] sm:text-[10px] font-black text-white/30 uppercase tracking-[0.15em] sm:tracking-[0.2em] text-center leading-tight">
        {label}
      </span>
    )}
  </div>
);

const BUSINESS_TYPES = [
  { color: '#FFCA28', icon: Database, label: 'Firebase' },
  { color: '#7FAF9A', icon: Coffee, label: 'Bakery' },
  { color: '#E8C547', icon: Palette, label: 'Artist' },
  { color: '#F28B82', icon: Camera, label: 'Photo' },
  { color: '#AECBFA', icon: Book, label: 'Education' },
  { color: '#EA4335', icon: Store, label: 'Retail' },
  { color: '#4285F4', icon: Zap, label: 'AI Core' },
  { color: '#D7AEFB', icon: Scissors, label: 'Craft' },
  { color: '#FBC02D', icon: Utensils, label: 'Catering' },
  { color: '#F06292', icon: Heart, label: 'Wellness' },
  { color: '#34A853', icon: ShoppingBag, label: 'Boutique' },
  { color: '#CCFF90', icon: Leaf, label: 'Nature' },
];

const Hero: React.FC = () => {

  return (
    <section className="relative pt-32 pb-4 sm:pt-48 sm:pb-8 bg-[#1F2933] overflow-hidden min-h-screen flex flex-col justify-center">
      {/* Dynamic Background Effects - Simplified on Mobile */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_40%,rgba(127,175,154,0.08),transparent_70%)]"></div>
      
      {/* The "Spotlight" / Aura */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] sm:w-[800px] sm:h-[400px] bg-[#7FAF9A]/5 blur-[80px] sm:blur-[120px] rounded-full pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 relative z-10 text-center flex-1 flex flex-col justify-center">
        {/* Refined "Empowering" Badge */}
        <div className="inline-flex items-center gap-3 px-6 py-2 rounded-full bg-white/5 border border-white/10 text-[#F4F1EC] text-[9px] sm:text-xs font-black uppercase tracking-[0.2em] mb-8 sm:mb-12 animate-in fade-in slide-in-from-bottom-4 duration-700 mx-auto">
          <div className="flex items-center gap-3">
            <Star size={12} fill="#E8C547" className="text-[#E8C547]" />
            <div className="w-[1px] h-3 bg-[#E8C547]/30"></div>
          </div>
          <span className="opacity-60 whitespace-nowrap">Empowering home-based businesses</span>
        </div>

        {/* Centralized Headline */}
        <div className="mb-6 sm:mb-10">
          <h1 className="text-4xl md:text-7xl lg:text-8xl font-extrabold text-[#F4F1EC] leading-[1.1] sm:leading-[1.05] tracking-tight animate-in fade-in slide-in-from-bottom-6 duration-1000">
            Tiny Systems That Run <br className="hidden sm:block" />
            <span className="text-[#E8C547] drop-shadow-[0_0_30px_rgba(232,197,71,0.2)]">Home Businesses</span>
          </h1>
        </div>

        {/* Sub-headline */}
        <div className="text-lg md:text-2xl font-black text-[#7FAF9A] mb-8 sm:mb-10 animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-100 tracking-tight">
          No App Stores. No Downloads. No Tech Headaches.
        </div>

        {/* Supporting Copy */}
        <p className="text-base md:text-xl text-[#F4F1EC]/50 mb-10 md:mb-14 max-w-xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200 font-medium">
          We design friction-free web systems that run instantly in any browser. 
          Give customers immediate service without the 'Install' button.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16 sm:mb-20 animate-in fade-in slide-in-from-bottom-10 duration-1000 delay-300">
          <a href="#build-system" className="group bg-[#E8C547] text-[#1F2933] px-10 py-5 rounded-[1.25rem] sm:rounded-[1.5rem] font-black text-lg sm:text-xl hover:bg-[#f5d461] transition-all shadow-2xl shadow-amber-900/40 w-full sm:w-auto text-center flex items-center justify-center gap-3">
            Get Started <span className="text-[10px] font-black opacity-40 uppercase tracking-widest border border-black/10 px-2 py-0.5 rounded-lg">$199</span>
          </a>
        </div>
      </div>

      {/* Marquee Icon Ribbon */}
      <div className="relative w-full overflow-hidden pb-4 sm:pb-8 opacity-70 sm:opacity-100 z-10">
        <div className="flex gap-8 sm:gap-16 animate-marquee whitespace-nowrap">
          <div className="flex gap-8 sm:gap-16 shrink-0">
            {BUSINESS_TYPES.map((biz, idx) => (
              <BusinessIcon key={idx} color={biz.color} icon={biz.icon} label={biz.label} />
            ))}
          </div>
          <div className="flex gap-8 sm:gap-16 shrink-0">
            {BUSINESS_TYPES.map((biz, idx) => (
              <BusinessIcon key={`dup-${idx}`} color={biz.color} icon={biz.icon} label={biz.label} />
            ))}
          </div>
        </div>
        
        {/* Edge Vignettes */}
        <div className="absolute inset-y-0 left-0 w-16 sm:w-48 bg-gradient-to-r from-[#1F2933] to-transparent z-20"></div>
        <div className="absolute inset-y-0 right-0 w-16 sm:w-48 bg-gradient-to-l from-[#1F2933] to-transparent z-20"></div>
      </div>

      {/* Explore Indicator */}
      <div className="flex flex-col items-center gap-1.5 text-white/10 animate-bounce mb-6 mt-4 z-10">
         <span className="text-[8px] font-black uppercase tracking-[0.3em]">Explore</span>
         <ChevronDown size={14} />
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes jump {
          0%, 100% { transform: translateY(0) scale(1.1); }
          50% { transform: translateY(-12px) scale(1.1); }
        }
        .animate-jump {
          animation: jump 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .animate-marquee {
          animation: marquee 15s linear infinite;
        }
        @media (min-width: 640px) {
          .animate-marquee {
            animation-duration: 25s;
          }
        }
        .animate-in {
          animation-fill-mode: both;
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-in-from-bottom {
          from { transform: translateY(20px); }
          to { transform: translateY(0); }
        }
        .fade-in { animation-name: fade-in; }
        .slide-in-from-bottom-4 { animation-name: fade-in, slide-in-from-bottom; }
        .slide-in-from-bottom-6 { animation-name: fade-in, slide-in-from-bottom; }
        .slide-in-from-bottom-8 { animation-name: fade-in, slide-in-from-bottom; }
        .slide-in-from-bottom-10 { animation-name: fade-in, slide-in-from-bottom; }
        .delay-100 { animation-delay: 100ms; }
        .delay-200 { animation-delay: 200ms; }
        .delay-300 { animation-delay: 300ms; }
      `}} />
    </section>
  );
};

export default Hero;
